import type { IHosRuleset } from "../interfaces/hosRuleset";
import {
    availToHours,
    convertMsToString,
    getCurrentCycle,
    getMinAvailability,
    getRuleSetById,
    HOURS,
    IHosAvailability,
    IHosPreciseAvailability,
    isDrivingBreakStatus,
    isOFFDutyStatus,
    IValidation,
    timeSpanToMs,
    hasUserDrivenInLatestWorkday,
    TranslatableDisplayStrings
} from "./helpers";

// Used to indicate when the requirement has been met. Replaced by checkmark icon when displayed on the UI.
export const DISPLAY_CHECKMARK = "✓";

export interface IDisplayObjectHos {
    availability: IHosAvailability;
    availabilityOutput: IAvailabilityOutput;
    displayCycleRestDuration: boolean;
    cycleRestDuration: string;
    cycleRestDurationMs: number;
    isDrivingBreak: boolean;
    drivingBreakDuration: string;
    drivingBreakDurationMs?: number;
    drivingBreakDurationLabel: string;
    drivingBreakInLabel: string;
    restBreakNeeded: boolean;
    restBreakNeededHours?: string;
    restBreakNeededMs?: number;
    offDutyNeeded: boolean;
    offDutyNeededHours?: string;
    offDutyNeededMs?: number;
}

interface IAvailabilityOutput {
    rest: string;
    restLabel?: string;
    driving: string;
    duty: string;
    workday: string;
    cycle: string;
    cycleRest: string;
    cycleDuty: string;
    cycleDriving: string;
}

export interface IAvailabilityDisplayInfo {
    name: string;
    label: string;
    value: string;
    htmlId: string;
    icon: {
        ariaLabel: string;
        href: string;
    };
    skipTranslateLabel?: boolean;
    ms?: number;
    limitsDrivingRange?: boolean;
}

function isUserOnDrivingBreak(ruleset: IHosRuleset | undefined, validation: IValidation, currentLog: any, drivingBreakDuration: number) {
    let restConfig = ruleset?.config.rest;
    let drivingBreakInMs = validation.availability.rest === undefined ? 0 : timeSpanToMs(validation.availability.rest);
    return (
        restConfig &&
        restConfig.onDutyCountTowardRest &&
        currentLog?.status &&
        isDrivingBreakStatus(currentLog.status) &&
        drivingBreakDuration !== 0 &&
        restConfig.betweenRestLimit &&
        hasUserDrivenInLatestWorkday(validation) &&
        // This is because the rest availability rounds up, so if less than a minute has elapsed, the remaining time will still show as max.
        (restConfig.betweenRestLimit === drivingBreakInMs || restConfig.betweenRestLimit - drivingBreakInMs >= 60 * 1000)
    );
}

function getAvailabilityOutput(availability: IHosAvailability, daysString: string, translatedHourUnit: string, translatedMinuteUnit: string) {
    let cycleRestHours = availToHours(availability.cycleRest) || 0;

    let availabilityOutput: IAvailabilityOutput = {
        rest: convertMsToString(availToHours(availability.rest) || 0, translatedHourUnit, translatedMinuteUnit),
        driving: convertMsToString(availToHours(availability.driving) || 0, translatedHourUnit, translatedMinuteUnit),
        duty: convertMsToString(availToHours(availability.duty) || 0, translatedHourUnit, translatedMinuteUnit),
        workday: convertMsToString(availToHours(availability.workday) || 0, translatedHourUnit, translatedMinuteUnit),
        cycle: convertMsToString(availToHours(availability.cycle) || 0, translatedHourUnit, translatedMinuteUnit),
        cycleRest:
            cycleRestHours < 24
                ? convertMsToString(cycleRestHours, translatedHourUnit, translatedMinuteUnit)
                : daysString.replace("{0}", Math.ceil(cycleRestHours / 24).toString()),
        cycleDuty: convertMsToString(availToHours(availability.dutySinceCycleRest) || 0, translatedHourUnit, translatedMinuteUnit),
        cycleDriving: convertMsToString(availToHours(availability.cycleDriving) || 0, translatedHourUnit, translatedMinuteUnit)
    };

    if (availability.restLabel) {
        availabilityOutput.restLabel = convertMsToString(availToHours(availability.restLabel) || 0, translatedHourUnit, translatedMinuteUnit);
    }
    return availabilityOutput;
}

export function hasDailyRequirements(ruleset: string) {
    return ruleset.toLowerCase().indexOf("canada") > -1;
}

function getDrivingBreakDurationValues(
    ruleResultdrivingBreakDuration: string | undefined,
    restLabel: string | undefined,
    translatedDrivingBreakDuration: string,
    translatedDrivingBreak: string,
    translatedHourUnit: string,
    translatedMinuteUnit: string
) {
    let drivingBreakDuration: number = ruleResultdrivingBreakDuration ? timeSpanToMs(ruleResultdrivingBreakDuration) / HOURS : 0;

    let requiredDrivingBreakToMarkAsComplete = restLabel ? timeSpanToMs(restLabel) / HOURS : 0.5;
    let isDrivingBreakComplete = drivingBreakDuration >= requiredDrivingBreakToMarkAsComplete;

    let drivingBreakDurationLabel = "";
    if (restLabel) {
        let restLabelStr = convertMsToString(availToHours(restLabel) || 0, translatedHourUnit, translatedMinuteUnit);
        drivingBreakDurationLabel = isDrivingBreakComplete
            ? translatedDrivingBreak.replace("{0}", restLabelStr)
            : translatedDrivingBreakDuration.replace("{0}", restLabelStr);
    } else {
        drivingBreakDurationLabel = isDrivingBreakComplete
            ? TranslatableDisplayStrings.DRIVING_BREAK
            : TranslatableDisplayStrings.DRIVING_BREAK_DURATION;
    }
    return { isDrivingBreakComplete, drivingBreakDuration, drivingBreakDurationLabel };
}

// todo: move all of this function's logic into the individual functions for availabilityDisplaySummary
// eslint-disable-next-line complexity
export function getDisplayObjectHos(
    validation: IValidation,
    ruleSetKey: string,
    currentLog: any,
    daysString: string,
    translatedHourUnit: string = TranslatableDisplayStrings.HOUR_UNIT,
    translatedMinuteUnit: string = TranslatableDisplayStrings.MINUTE_UNIT,
    translatedAlternateDrivingBreakDuration: string = TranslatableDisplayStrings.ALTERNATE_DRIVING_BREAK_DURATION,
    translatedAlternateDrivingBreak: string = TranslatableDisplayStrings.ALTERNATE_DRIVING_BREAK,
    translatedAlternateDrivingBreakIn: string = TranslatableDisplayStrings.ALTERNATE_DRIVING_BREAK_IN,
    completedString = `<span><svg class="svgIcon big" role="img" fill="#419253"><use xlink:href='#drive-check'></use></svg></span>`
): IDisplayObjectHos {
    let currentCycle = getCurrentCycle(validation.cycleSummaries);
    let cycleRestDuration: number = currentCycle?.consecutiveRest ? timeSpanToMs(currentCycle.consecutiveRest) / HOURS : 0;
    let { isDrivingBreakComplete, drivingBreakDuration, drivingBreakDurationLabel } = getDrivingBreakDurationValues(
        validation.availability.drivingBreakDuration,
        validation.availability.restLabel,
        translatedAlternateDrivingBreakDuration,
        translatedAlternateDrivingBreak,
        translatedHourUnit,
        translatedMinuteUnit
    );
    let ruleset = getRuleSetById(ruleSetKey);
    let displayCycleRestDuration = ruleset?.config.cycle && (!currentLog || isOFFDutyStatus(currentLog.status)) && cycleRestDuration !== 0;
    let isDrivingBreak = isUserOnDrivingBreak(ruleset, validation, currentLog, drivingBreakDuration);

    let offDutyNeededHours = availToHours(validation.offDutyNeeded);
    let restBreakNeededHours = availToHours(validation.restBreakNeeded);
    const ENOUGH_REST_LENGTH = 100;
    return {
        // availability
        availability: validation.availability,
        availabilityOutput: getAvailabilityOutput(validation.availability, daysString, translatedHourUnit, translatedMinuteUnit),
        // cycleRest
        displayCycleRestDuration: !!displayCycleRestDuration && cycleRestDuration <= ENOUGH_REST_LENGTH,
        cycleRestDuration: convertMsToString(cycleRestDuration, translatedHourUnit, translatedMinuteUnit, true),
        cycleRestDurationMs: cycleRestDuration,
        // driving break
        isDrivingBreak,
        drivingBreakDuration: isDrivingBreakComplete
            ? completedString
            : convertMsToString(drivingBreakDuration, translatedHourUnit, translatedMinuteUnit, true),
        drivingBreakDurationMs: isDrivingBreakComplete ? undefined : drivingBreakDuration,
        drivingBreakDurationLabel: drivingBreakDurationLabel,
        drivingBreakInLabel: validation.availability.restLabel
            ? translatedAlternateDrivingBreakIn.replace(
                  "{0}",
                  convertMsToString(availToHours(validation.availability.restLabel) || 0, translatedHourUnit, translatedMinuteUnit)
              )
            : TranslatableDisplayStrings.DRIVING_BREAK_IN,
        // dailyRest
        restBreakNeeded: !!restBreakNeededHours || hasDailyRequirements(ruleSetKey),
        restBreakNeededHours: restBreakNeededHours ? convertMsToString(restBreakNeededHours, translatedHourUnit, translatedMinuteUnit) : undefined,
        restBreakNeededMs: restBreakNeededHours ?? undefined,
        // dailyOff
        offDutyNeeded: !!offDutyNeededHours || hasDailyRequirements(ruleSetKey),
        offDutyNeededHours: offDutyNeededHours ? convertMsToString(offDutyNeededHours, translatedHourUnit, translatedMinuteUnit) : undefined,
        offDutyNeededMs: offDutyNeededHours ?? undefined
    };
}

function getDriveIconHrefByAvailabilityId(id: string): string {
    switch (id) {
        case "avail-rest-duration":
            return "#drive-coffee";
        case "avail-drive-break-duration":
        case "needed-break":
            return "#drive-coffee";
        case "avail-rest":
        case "avail-alternate-rest":
            return "#drive-stop";
        case "avail-driving":
            return "#drive-steering-wheel";
        case "avail-duty":
        case "avail-cycleDuty":
            return "#drive-briefcase";
        case "avail-workday":
            return "#drive-tools";
        case "avail-cycle":
            return "#drive-ccw";
        case "avail-cycleDriving":
            return "#drive-truck";
        case "avail-cycleRest":
            return "#drive-clock";
        case "needed-off":
            return "#drive-off";
        default: {
            if (id.indexOf("avail-drive-break-duration") === 0) {
                return "#drive-coffee";
            } else if (id.indexOf("avail-alternate-rest") === 0) {
                return "#drive-stop";
            }
            return "";
        }
    }
}

const driveIconHrefToAriaLabel: { [key: string]: string } = {
    "#drive-bed": "Rest duration icon",
    "#drive-coffee": "Driving break duration icon",
    "#drive-stop": "Driving break in icon",
    "#drive-steering-wheel": "Driving left icon",
    "#drive-briefcase": "Duty left icon",
    "#drive-tools": "Workday left icon",
    "#drive-ccw": "Cycle icon",
    "#drive-truck": "Cycle driving icon",
    "#drive-clock": "Cycle rest icon",
    "#drive-ok": "Complete",
    "#drive-off": "Off duty required icon"
};

const uiAvailabilityBuilder = (
    name: TAvailabilityDisplayItemName,
    htmlId: string,
    label: string,
    value: string,
    ms?: number,
    skipTranslateLabel?: boolean,
    limitsDrivingRange?: boolean
): IAvailabilityDisplayInfo => {
    let href = getDriveIconHrefByAvailabilityId(htmlId);
    let ariaLabel = driveIconHrefToAriaLabel[href] ?? "";
    let icon = { href, ariaLabel };
    let result: IAvailabilityDisplayInfo = { htmlId: htmlId, name, label, value, icon };
    if (ms !== undefined && ms !== null) {
        result.ms = ms;
    }
    if (skipTranslateLabel) {
        result.skipTranslateLabel = true;
    }
    if (limitsDrivingRange) {
        result.limitsDrivingRange = true;
    }
    return result;
};

type TAvailabilityDisplayItemName =
    | "cycleRestDuration"
    | "drivingBreakDuration"
    | "rest"
    | "driving"
    | "duty"
    | "workday"
    | "cycle"
    | "cycleDriving"
    | "cycleRest"
    | "cycleDuty"
    | "dailyRestNeeded"
    | "dailyOffNeeded";
type TAvailabilityDisplayItemFn = (
    displayObject: IDisplayObjectHos,
    preciseAvailability: IHosPreciseAvailability
) => IAvailabilityDisplayInfo | undefined;
const availabilityItemFunctions: { [key in TAvailabilityDisplayItemName]: TAvailabilityDisplayItemFn } = {
    cycleRestDuration: (displayObject: IDisplayObjectHos, _: IHosPreciseAvailability): IAvailabilityDisplayInfo | undefined => {
        if (displayObject.displayCycleRestDuration) {
            return uiAvailabilityBuilder(
                "cycleRestDuration",
                "avail-rest-duration",
                TranslatableDisplayStrings.REST_DURATION,
                displayObject.cycleRestDuration
            );
        }
        return undefined;
    },
    drivingBreakDuration: (displayObject: IDisplayObjectHos, _: IHosPreciseAvailability): IAvailabilityDisplayInfo | undefined => {
        if (displayObject.isDrivingBreak) {
            let [id, drivingBreakDurationLabel, skipTranslateLabel] = displayObject.availabilityOutput.restLabel
                ? [`avail-drive-break-duration-${displayObject.availabilityOutput.restLabel}`, displayObject.drivingBreakDurationLabel, true]
                : ["avail-drive-break-duration", displayObject.drivingBreakDurationLabel, false];
            return uiAvailabilityBuilder(
                "drivingBreakDuration",
                id,
                drivingBreakDurationLabel,
                displayObject.drivingBreakDuration,
                displayObject.drivingBreakDurationMs,
                skipTranslateLabel
            );
        }
        return undefined;
    },
    rest: (displayObject: IDisplayObjectHos, preciseAvailability: IHosPreciseAvailability): IAvailabilityDisplayInfo | undefined => {
        if (preciseAvailability.rest === undefined) {
            return undefined;
        }
        let [id, drivingBreakInLabel, skipTranslateLabel] = displayObject.availabilityOutput.restLabel
            ? [`avail-alternate-rest-${displayObject.availabilityOutput.restLabel}`, displayObject.drivingBreakInLabel, true]
            : ["avail-rest", TranslatableDisplayStrings.DRIVING_BREAK_IN, false];
        return uiAvailabilityBuilder(
            "rest",
            id,
            drivingBreakInLabel,
            displayObject.availabilityOutput.rest,
            preciseAvailability.rest,
            skipTranslateLabel
        );
    },
    driving: (displayObject: IDisplayObjectHos, preciseAvailability: IHosPreciseAvailability): IAvailabilityDisplayInfo | undefined => {
        return displayObject.availability.driving === undefined
            ? undefined
            : uiAvailabilityBuilder(
                  "driving",
                  "avail-driving",
                  TranslatableDisplayStrings.DRIVING_LEFT,
                  displayObject.availabilityOutput.driving,
                  preciseAvailability.driving
              );
    },
    duty: (displayObject: IDisplayObjectHos, preciseAvailability: IHosPreciseAvailability): IAvailabilityDisplayInfo | undefined => {
        return displayObject.availability.duty === undefined
            ? undefined
            : uiAvailabilityBuilder(
                  "duty",
                  "avail-duty",
                  TranslatableDisplayStrings.DUTY_LEFT,
                  displayObject.availabilityOutput.duty,
                  preciseAvailability.duty
              );
    },
    workday: (displayObject: IDisplayObjectHos, preciseAvailability: IHosPreciseAvailability): IAvailabilityDisplayInfo | undefined => {
        return displayObject.availability.workday === undefined
            ? undefined
            : uiAvailabilityBuilder(
                  "workday",
                  "avail-workday",
                  TranslatableDisplayStrings.WORKDAY_LEFT,
                  displayObject.availabilityOutput.workday,
                  preciseAvailability.workday
              );
    },
    cycle: (displayObject: IDisplayObjectHos, preciseAvailability: IHosPreciseAvailability): IAvailabilityDisplayInfo | undefined => {
        return displayObject.availability.cycle === undefined
            ? undefined
            : uiAvailabilityBuilder(
                  "cycle",
                  "avail-cycle",
                  TranslatableDisplayStrings.CYCLE_LEFT,
                  displayObject.availabilityOutput.cycle,
                  preciseAvailability.cycle
              );
    },
    cycleDriving: (displayObject: IDisplayObjectHos, preciseAvailability: IHosPreciseAvailability): IAvailabilityDisplayInfo | undefined => {
        return displayObject.availability.cycleDriving === undefined
            ? undefined
            : uiAvailabilityBuilder(
                  "cycleDriving",
                  "avail-cycleDriving",
                  TranslatableDisplayStrings.CYCLE_DRIVING_LEFT,
                  displayObject.availabilityOutput.cycleDriving,
                  preciseAvailability.cycleDriving
              );
    },
    cycleRest: (displayObject: IDisplayObjectHos, preciseAvailability: IHosPreciseAvailability): IAvailabilityDisplayInfo | undefined => {
        return displayObject.availability.cycleRest === undefined
            ? undefined
            : uiAvailabilityBuilder(
                  "cycleRest",
                  "avail-cycleRest",
                  TranslatableDisplayStrings.TIME_LEFT_UNTIL_24_HOUR_REST,
                  displayObject.availabilityOutput.cycleRest,
                  preciseAvailability.cycleRest
              );
    },
    cycleDuty: (displayObject: IDisplayObjectHos, preciseAvailability: IHosPreciseAvailability): IAvailabilityDisplayInfo | undefined => {
        return displayObject.availability.dutySinceCycleRest === undefined
            ? undefined
            : uiAvailabilityBuilder(
                  "cycleDuty",
                  "avail-cycleDuty",
                  TranslatableDisplayStrings.DUTY_LEFT_UNTIL_24_HOUR_REST,
                  displayObject.availabilityOutput.cycleDuty,
                  preciseAvailability.dutySinceCycleRest
              );
    },
    dailyRestNeeded: (displayObject: IDisplayObjectHos, _: IHosPreciseAvailability): IAvailabilityDisplayInfo | undefined => {
        if (!displayObject.restBreakNeeded) {
            return undefined;
        }
        return uiAvailabilityBuilder(
            "dailyRestNeeded",
            "needed-break",
            TranslatableDisplayStrings.REST_REQUIRED,
            displayObject.restBreakNeededHours ?? DISPLAY_CHECKMARK,
            displayObject.restBreakNeededMs
        );
    },
    dailyOffNeeded: (displayObject: IDisplayObjectHos, _: IHosPreciseAvailability): IAvailabilityDisplayInfo | undefined => {
        if (!displayObject.offDutyNeeded) {
            return undefined;
        }
        return uiAvailabilityBuilder(
            "dailyOffNeeded",
            "needed-off",
            TranslatableDisplayStrings.OFF_DUTY_REQUIRED,
            displayObject.offDutyNeededHours ?? DISPLAY_CHECKMARK,
            displayObject.offDutyNeededMs
        );
    }
};

const isAvailabilityDisplayInfo = (v: IAvailabilityDisplayInfo | undefined): v is IAvailabilityDisplayInfo => v !== undefined;

export const getAvailabilityDisplaySummary = (
    displayObject: IDisplayObjectHos,
    preciseAvailability: IHosPreciseAvailability = {}
): IAvailabilityDisplayInfo[] => {
    // This is the order that the items will appear the UI
    const rowNames: TAvailabilityDisplayItemName[] = [
        "cycleRestDuration",
        "drivingBreakDuration",
        "rest",
        "driving",
        "duty",
        "workday",
        "cycle",
        "cycleDriving",
        "cycleRest",
        "cycleDuty",
        "dailyRestNeeded",
        "dailyOffNeeded"
    ];
    let summary = rowNames.map(rowName => availabilityItemFunctions[rowName](displayObject, preciseAvailability)).filter(isAvailabilityDisplayInfo);

    let driveRangeLimiterName = getMinAvailability(displayObject.availability).name ?? "driving";
    let driveRangeLimiter = summary.find(a => a.name === driveRangeLimiterName);
    driveRangeLimiter && (driveRangeLimiter.limitsDrivingRange = true);

    return summary;
};
